import React, {createContext, useContext, useState, useCallback} from 'react';
import {APP_API_BASE, APP_API_MODE, requiresAppApiAuth} from '../helpers/appApiConfig';

const TOKEN_KEY = 'vocabmaster_auth_token';
const USER_KEY = 'vocabmaster_auth_user';

const AuthContext = createContext(null);

export const AuthProvider = ({children}) => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem(USER_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const startLogin = useCallback(async () => {
    // In dev remote/hybrid mode, force localhost callback so the local OAuth app
    // (vocabmaster-local) is selected server-side. In all other cases, use the
    // current origin so production login works unchanged.
    const redirectUri =
      process.env.NODE_ENV === 'development' && APP_API_MODE === 'remote'
        ? 'http://localhost:3002/auth/callback'
        : `${window.location.origin}/auth/callback`;
    const query = new URLSearchParams({redirectUri});
    const res = await fetch(`${APP_API_BASE}/api/auth/login?${query.toString()}`);
    if (!res.ok) throw new Error('Failed to initiate login');
    const data = await res.json();
    sessionStorage.setItem('oauth_state', data.state);
    window.location.href = data.url;
  }, []);

  const handleCallback = useCallback(async (code, state) => {
    const savedState = sessionStorage.getItem('oauth_state');
    if (savedState && savedState !== state) {
      throw new Error('OAuth state mismatch — possible CSRF');
    }
    sessionStorage.removeItem('oauth_state');

    const res = await fetch(`${APP_API_BASE}/api/auth/callback`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({code, state}),
    });
    if (!res.ok) {
      let detail = 'Authentication failed';
      try {
        const body = await res.json();
        detail = body.detail || body.error || body.title || detail;
      } catch {}
      throw new Error(detail);
    }
    const data = await res.json();

    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    setToken(data.token);
    setUser(data.user);
    return data;
  }, []);

  const logout = useCallback(async () => {
    const currentToken = localStorage.getItem(TOKEN_KEY);
    if (currentToken) {
      try {
        await fetch(`${APP_API_BASE}/api/auth/logout`, {
          method: 'POST',
          headers: {Authorization: `Bearer ${currentToken}`},
        });
      } catch (_) {}
    }
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setToken(null);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{
      token,
      user,
      isAuthenticated: requiresAppApiAuth() ? !!token : true,
      startLogin,
      handleCallback,
      logout,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
